import { HTMLAttributes, forwardRef, ReactNode } from 'react';

import { cn } from '../utils/cn';

// Table Root
export interface TableProps extends HTMLAttributes<HTMLTableElement> {
  variant?: 'default' | 'striped';
}

export const Table = forwardRef<HTMLTableElement, TableProps>(
  ({ className, variant = 'default', ...props }, ref) => (
    <div className="w-full overflow-x-auto rounded-[var(--ids-radius-xl,20px)] border border-[color:var(--ids-border,rgba(255,255,255,0.08))] bg-[color:var(--ids-surface,#141B2D)] shadow-[var(--ids-elev-1,0_1px_2px_rgba(0,0,0,0.4))]">
      <table
        ref={ref}
        className={cn(
          'w-full border-collapse text-left text-sm',
          {
            '[&_tbody_tr:nth-child(odd)]:bg-[color:var(--ids-bg-subtle,#0F1626)]':
              variant === 'striped',
          },
          className
        )}
        {...props}
      />
    </div>
  )
);

Table.displayName = 'Table';

// Table Header
export type TableHeaderProps = HTMLAttributes<HTMLTableSectionElement>;

export const TableHeader = forwardRef<HTMLTableSectionElement, TableHeaderProps>(
  ({ className, ...props }, ref) => (
    <thead
      ref={ref}
      className={cn(
        'bg-[color:var(--ids-bg-subtle,#0F1626)] border-b border-[color:var(--ids-border,rgba(255,255,255,0.08))]',
        className
      )}
      {...props}
    />
  )
);

TableHeader.displayName = 'TableHeader';

// Table Body
export type TableBodyProps = HTMLAttributes<HTMLTableSectionElement>;

export const TableBody = forwardRef<HTMLTableSectionElement, TableBodyProps>(
  ({ className, ...props }, ref) => (
    <tbody
      ref={ref}
      className={cn('divide-y divide-[color:var(--ids-border,rgba(255,255,255,0.08))]', className)}
      {...props}
    />
  )
);

TableBody.displayName = 'TableBody';

// Table Row
export interface TableRowProps extends HTMLAttributes<HTMLTableRowElement> {
  selected?: boolean;
  hoverable?: boolean;
}

export const TableRow = forwardRef<HTMLTableRowElement, TableRowProps>(
  ({ className, selected, hoverable = true, ...props }, ref) => (
    <tr
      ref={ref}
      className={cn(
        'transition-colors',
        {
          'hover:bg-[color:var(--ids-bg-subtle,#0F1626)]': hoverable,
          'bg-[color:rgba(var(--ids-brand-rgb,59,130,246),0.10)]': selected,
        },
        className
      )}
      {...props}
    />
  )
);

TableRow.displayName = 'TableRow';

// Table Head Cell
export interface TableHeadProps extends HTMLAttributes<HTMLTableCellElement> {
  sortable?: boolean;
  sorted?: 'asc' | 'desc' | null;
  onSort?: () => void;
}

export const TableHead = forwardRef<HTMLTableCellElement, TableHeadProps>(
  ({ className, sortable, sorted, onSort, children, ...props }, ref) => (
    <th
      ref={ref}
      className={cn(
        'px-4 py-3 text-[10.5px] font-semibold uppercase tracking-[0.07em] text-[color:var(--ids-text-subtle,#7E8DA6)]',
        sortable && 'cursor-pointer select-none hover:text-[color:var(--ids-text,#F8FAFC)]',
        className
      )}
      onClick={sortable ? onSort : undefined}
      {...props}
    >
      <div className="flex items-center gap-2">
        {children}
        {sortable && (
          <span className="inline-flex flex-col">
            <svg
              className={cn(
                'w-3 h-3 -mb-1',
                sorted === 'asc'
                  ? 'text-[color:var(--ids-brand,#3B82F6)]'
                  : 'text-[color:var(--ids-text-subtle,#7E8DA6)]'
              )}
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path d="M7 14l3-3 3 3V7l-3 3-3-3v7z" transform="rotate(180 10 10)" />
            </svg>
            <svg
              className={cn(
                'w-3 h-3',
                sorted === 'desc'
                  ? 'text-[color:var(--ids-brand,#3B82F6)]'
                  : 'text-[color:var(--ids-text-subtle,#7E8DA6)]'
              )}
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path d="M7 14l3-3 3 3V7l-3 3-3-3v7z" />
            </svg>
          </span>
        )}
      </div>
    </th>
  )
);

TableHead.displayName = 'TableHead';

// Table Cell
export type TableCellProps = HTMLAttributes<HTMLTableCellElement>;

export const TableCell = forwardRef<HTMLTableCellElement, TableCellProps>(
  ({ className, ...props }, ref) => (
    <td
      ref={ref}
      className={cn('px-4 py-3 text-sm text-[color:var(--ids-text-muted,#94A3B8)]', className)}
      {...props}
    />
  )
);

TableCell.displayName = 'TableCell';

// Empty State
export interface TableEmptyProps extends HTMLAttributes<HTMLTableRowElement> {
  colSpan: number;
  icon?: ReactNode;
  message?: string;
  description?: string;
}

export const TableEmpty = forwardRef<HTMLTableRowElement, TableEmptyProps>(
  ({ className, colSpan, icon, message = 'No data', description, ...props }, ref) => (
    <tr ref={ref} className={className} {...props}>
      <td colSpan={colSpan} className="px-4 py-12 text-center">
        {icon && (
          <div className="flex justify-center mb-3 text-[color:var(--ids-text-subtle,#7E8DA6)]">
            {icon}
          </div>
        )}
        <p className="text-[color:var(--ids-text-muted,#94A3B8)] font-medium">{message}</p>
        {description && (
          <p className="text-[color:var(--ids-text-subtle,#7E8DA6)] text-sm mt-1">{description}</p>
        )}
      </td>
    </tr>
  )
);

TableEmpty.displayName = 'TableEmpty';

// Loading State
export interface TableLoadingProps extends HTMLAttributes<HTMLTableRowElement> {
  colSpan: number;
  rows?: number;
}

export const TableLoading = forwardRef<HTMLTableRowElement, TableLoadingProps>(
  ({ className, colSpan, rows = 3, ...props }, ref) => (
    <>
      {Array.from({ length: rows }).map((_, i) => (
        <tr
          key={i}
          ref={i === 0 ? ref : undefined}
          className={cn('animate-pulse', className)}
          {...props}
        >
          <td colSpan={colSpan} className="px-4 py-3">
            <div className="h-4 bg-[color:var(--ids-bg-subtle,#0F1626)] rounded-[var(--ids-radius-sm,6px)] w-full" />
          </td>
        </tr>
      ))}
    </>
  )
);

TableLoading.displayName = 'TableLoading';
